import { useEffect, useMemo, useState } from 'react';
import { NavLink, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faThumbsUp, faThumbsDown, faListUl, faFileLines } from '@fortawesome/free-solid-svg-icons';
import Seo from '../../components/Seo/Seo';
import HelpHeader from './HelpHeader';
import PublicBreadcrumb from '../../components/PublicBreadcrumb/PublicBreadcrumb';
import HelpSidebarNav from './HelpSidebarNav';
import { resolveHelpIcon, helpTint, helpTintBg } from './helpDisplay';
import MissionSection from '../../components/Home/MissionSection';
import Footer from '../../components/Home/Footer';
import GDPRConsent from '../../components/Home/GDPRConsent';
import { API_BASE_URL } from '../../api';
import '../Help.scss';

const DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

const slugifyHeading = (str) => str
	.toLowerCase()
	.normalize('NFD').replace(DIACRITICS, '')
	.replace(/[^a-z0-9]+/g, '-')
	.replace(/^-+|-+$/g, '') || 'section';

// Post-processes the article's raw HTML before display:
// - rewrites h2/h3 headings with stable ids and returns the flat list to
//   render as the right-hand "Sommaire" nav (content is authored rich text
//   with no ids of its own, so this is computed fresh from each article).
// - wraps every <img> in a <figure> (the full-width framed card — see
//   Help.scss) so all screenshots get the same treatment, and adds a
//   <figcaption> when the image has a caption (its alt attribute, set from
//   the WYSIWYG — see RichTextEditor.js) — skips images already wrapped by
//   hand.
const useArticleToc = (html) => useMemo(() => {
	if (!html) return { html, toc: [] };
	const doc = new DOMParser().parseFromString(html, 'text/html');
	const seen = new Map();
	const toc = [];
	doc.querySelectorAll('h2, h3').forEach((heading) => {
		const text = heading.textContent.trim();
		if (!text) return;
		const base = slugifyHeading(text);
		const count = seen.get(base) || 0;
		seen.set(base, count + 1);
		const id = count > 0 ? `${base}-${count}` : base;
		heading.id = id;
		toc.push({ id, text, level: heading.tagName === 'H3' ? 3 : 2 });
	});

	doc.querySelectorAll('img').forEach((img) => {
		if (img.closest('figure')) return;
		const caption = img.getAttribute('alt')?.trim();
		const figure = doc.createElement('figure');
		img.replaceWith(figure);
		figure.appendChild(img);
		if (caption) {
			const figcaption = doc.createElement('figcaption');
			figcaption.textContent = caption;
			figure.appendChild(figcaption);
		}
	});

	return { html: doc.body.innerHTML, toc };
}, [html]);

const HelpPost = () => {
	const { categorySlug, postSlug } = useParams();
	const { i18n } = useTranslation();
	const [category, setCategory] = useState(null);
	const [post, setPost] = useState(null);
	const [categoryArticles, setCategoryArticles] = useState([]);
	const [allCategories, setAllCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);
	const [feedback, setFeedback] = useState(null);
	const { html: articleHtml, toc } = useArticleToc(post?.content);
	const related = categoryArticles.filter(p => p.slug !== postSlug).slice(0, 3);
	const [activeHeadingId, setActiveHeadingId] = useState('');

	useEffect(() => {
		setLoading(true);
		setNotFound(false);
		Promise.all([
			axios.get(`${API_BASE_URL}/api/v1/help/categories/${categorySlug}`),
			axios.get(`${API_BASE_URL}/api/v1/help/articles/${postSlug}`),
			axios.get(`${API_BASE_URL}/api/v1/help/categories/${categorySlug}/articles`),
			axios.get(`${API_BASE_URL}/api/v1/help/categories`),
		])
			.then(([categoryRes, articleRes, articlesRes, categoriesRes]) => {
				if (articleRes.data.categoryId !== categoryRes.data.id) {
					setNotFound(true);
					return;
				}
				setCategory(categoryRes.data);
				setPost(articleRes.data);
				setCategoryArticles(articlesRes.data);
				setAllCategories(categoriesRes.data);
			})
			.catch(() => setNotFound(true))
			.finally(() => setLoading(false));
	}, [categorySlug, postSlug]);

	useEffect(() => { setFeedback(null); }, [postSlug]);

	// Scroll-spy for the "Sommaire" — keeps the link for the heading currently
	// at the top of the viewport highlighted, mirroring NavBar.js's same pattern
	// for the landing page's section nav.
	useEffect(() => {
		if (toc.length === 0) return;
		const onScroll = () => {
			let current = '';
			for (const item of toc) {
				const el = document.getElementById(item.id);
				if (el && el.getBoundingClientRect().top <= 120) current = item.id;
			}
			setActiveHeadingId(current);
		};
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, [toc]);

	if (notFound) return <Navigate to={`/${i18n.language}/help`} replace />;
	if (loading || !category || !post) return null;

	return (
		<div className="help-page">
			<Seo
				title={`${post.title} — Centre d'aide Saydalani`}
				description={post.excerpt}
				path={`/${i18n.language}/help/${category.slug}/${post.slug}`}
			/>

			<HelpHeader />
			<PublicBreadcrumb
				className="pub-crumbs--bar"
				maxWidth={1360}
				gutter="24px"
				items={[
					{ label: "Centre d'aide", to: `/${i18n.language}/help` },
					{ label: category.label, to: `/${i18n.language}/help/${category.slug}` },
					{ label: post.title },
				]}
			/>

			<div className="help-article-hero">

				<span className="help-article__category" style={{ background: helpTintBg(category.color), color: helpTint(category.color) }}>
					<FontAwesomeIcon icon={resolveHelpIcon(category.icon)} />
					{category.label}
				</span>
				<h1>{post.title}</h1>
			</div>

			<div className="help-container help-container--article">
				<div className="help-article-layout">
					<aside className="help-sidebar">
						<HelpSidebarNav
							categories={allCategories}
							activeCategory={category}
							activeArticles={categoryArticles}
							activePostSlug={postSlug}
						/>
					</aside>

					<article className="help-article">
						<div className="help-article__body" dangerouslySetInnerHTML={{ __html: articleHtml }} />
					</article>

					{toc.length >= 1 && (
						<aside className="help-toc">
							<p className="help-toc__title">
								<FontAwesomeIcon icon={faListUl} />
								Sommaire
							</p>
							<nav>
								{toc.map(item => (
									<a
										key={item.id}
										href={`#${item.id}`}
										className={`help-toc__link help-toc__link--h${item.level}${item.id === activeHeadingId ? ' help-toc__link--active' : ''}`}
									>
										{item.text}
									</a>
								))}
							</nav>
						</aside>
					)}
				</div>

				<div className="help-feedback">
					{feedback ? (
						<p>Merci pour votre retour !</p>
					) : (
						<>
							<p>Cet article vous a-t-il été utile ?</p>
							<div className="help-feedback__buttons">
								<button type="button" onClick={() => setFeedback('yes')}>
									<FontAwesomeIcon icon={faThumbsUp} /> Oui
								</button>
								<button type="button" onClick={() => setFeedback('no')}>
									<FontAwesomeIcon icon={faThumbsDown} /> Non
								</button>
							</div>
						</>
					)}
				</div>

				<NavLink to={`/${i18n.language}/help/${category.slug}`} className="help-back-link">
					<FontAwesomeIcon icon={faChevronLeft} />
					Retour à {category.label}
				</NavLink>

				{related.length > 0 && (
					<div className="help-related">
						<p className="help-related__title">Voir aussi</p>
						<div className="help-post-grid">
							{related.map(p => (
								<NavLink key={p.id} to={`/${i18n.language}/help/${category.slug}/${p.slug}`} className="help-post-card">
									<span className="help-post-card__thumb">
										{p.imageUrl ? (
											<img src={p.imageUrl} alt="" />
										) : (
											<span
												className="help-post-card__thumb-fallback"
												style={{ background: helpTintBg(category.color), color: helpTint(category.color) }}
											>
												<FontAwesomeIcon icon={faFileLines} />
											</span>
										)}
									</span>
									<span className="help-post-card__body">
										<span className="help-post-card__title">{p.title}</span>
										<span className="help-post-card__excerpt">{p.excerpt}</span>
									</span>
								</NavLink>
							))}
						</div>
					</div>
				)}
			</div>

			<MissionSection />
			<Footer />
			<GDPRConsent />
		</div>
	);
};

export default HelpPost;
